import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Search, Plus, Edit3, Trash2, CheckCircle,
  ThumbsUp, ThumbsDown, Loader2,
  FileText, X, Sparkles, Link as LinkIcon, ChevronLeft, ChevronRight,
  Tag
} from 'lucide-react';
import toast from 'react-hot-toast';
import ApiService from '../../services/ApiService';
import ReviewCenter from './ReviewCenter';
import { FAQListSkeleton } from '../ui/FAQSkeletons';

function FAQManagement() {
  const [activeTab, setActiveTab] = useState('list'); // 'list', 'review'
  const [viewMode, setViewMode] = useState('list'); // 'list', 'create', 'edit'

  // Data States
  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filter States
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('all');
  const [filterTagInput, setFilterTagInput] = useState('');
  const [filterTagSuggestions, setFilterTagSuggestions] = useState([]);
  const [isFilterTagDropdownOpen, setIsFilterTagDropdownOpen] = useState(false);

  // Selection State
  const [selectedFAQIds, setSelectedFAQIds] = useState([]);

  // Editor State
  const [currentFAQ, setCurrentFAQ] = useState(null); // For edit mode
  const [editorForm, setEditorForm] = useState({
    question: '',
    answer: '',
    status: 'public',
    category_ids: [],
    tags: [],
    author: '',
    needs_review: false
  });
  const [tagInput, setTagInput] = useState('');
  const [tagSuggestions, setTagSuggestions] = useState([]);

  // New Category State
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isSavingCategory, setIsSavingCategory] = useState(false);

  // Bulk Action States
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // Auto Link State
  const [showAutoLinkModal, setShowAutoLinkModal] = useState(false);
  const [autoLinks, setAutoLinks] = useState([{ keyword: '', url: '' }]);

  // Optimization State
  const [showOptimizationModal, setShowOptimizationModal] = useState(false);
  const [selectedOptimization, setSelectedOptimization] = useState('');

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const loadFAQs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (tagFilter !== 'all') params.tags = tagFilter;

      const data = await ApiService.getFAQs(params);
      setFaqs(data.faqs || []);
    } catch (error) {
      toast.error('Failed to load FAQs');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);


  const loadCategories = async () => {
    try {
      const data = await ApiService.getCategories();
      if (data && data.categories) {
        setCategories(data.categories);
      }
    } catch (error) {
      console.error('Failed to load categories', error);
    }
  };

  const loadTags = async () => {
    try {
      const data = await ApiService.getTags();
      if (data && data.tags) {
        setAllTags(data.tags.map(t => t.name).sort());
      }
    } catch (error) {
      console.error('Failed to load tags', error);
    }
  };

  const handleFilterTagInputChange = async (e) => {
    const value = e.target.value;
    setFilterTagInput(value);
    setIsFilterTagDropdownOpen(true);
    
    if (value.trim().length > 0) {
      try {
        const data = await ApiService.autocompleteTags(value);
        setFilterTagSuggestions(data.tags || []);
      } catch (err) {
        console.error(err);
      }
    } else {
      setFilterTagSuggestions([]);
    }
  };

  const selectFilterTag = (tag) => {
    if (tag === 'all') {
      setTagFilter('all');
      setFilterTagInput('');
    } else {
      setTagFilter(tag);
      setFilterTagInput(tag);
    }
    setIsFilterTagDropdownOpen(false);
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error('Category name is required');
      return;
    }

    setIsSavingCategory(true);
    try {
      const response = await ApiService.createCategory({ 
        name: newCategoryName.trim()
      });
      
      if (response && response.success) {
        toast.success('Category created successfully');
        await loadCategories();
        const createdCatId = response.category?.category_id;
        if (createdCatId) {
          setEditorForm(prev => ({ ...prev, category_ids: [...(prev.category_ids || []), createdCatId] }));
        }
        setIsAddingCategory(false);
        setNewCategoryName('');
      } else {
        toast.error(response?.message || 'Failed to create category');
      }
    } catch (error) {
      toast.error('Error creating category');
      console.error(error);
    } finally {
      setIsSavingCategory(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, categoryFilter, tagFilter]);

  useEffect(() => {
    if (activeTab === 'list') {
      loadCategories();
      loadTags();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'list') {
      loadFAQs();
    }
  }, [activeTab, loadFAQs]);

  // --- List View Helpers ---

  // Helper: get assigned category_ids from a FAQ (reads from the new `categories` array)
  const getFaqCategoryIds = (faq) => {
    if (Array.isArray(faq.categories) && faq.categories.length > 0) return faq.categories;
    // backwards compat: old schema used category_ids or category_id
    if (Array.isArray(faq.category_ids) && faq.category_ids.length > 0) return faq.category_ids;
    if (faq.category_id) return [faq.category_id];
    return [];
  };

  const filteredFAQs = useMemo(() => {
    return faqs.filter(faq => {
      const matchesSearch = faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || faq.status === statusFilter;
      const faqCatIds = getFaqCategoryIds(faq);
      const matchesCategory = categoryFilter === 'all'
        ? true
        : categoryFilter === 'none'
          ? faqCatIds.length === 0
          : faqCatIds.includes(categoryFilter);
      const matchesTag = tagFilter === 'all' || (faq.tags && faq.tags.includes(tagFilter));

      return matchesSearch && matchesStatus && matchesCategory && matchesTag;
    });
  }, [faqs, searchQuery, statusFilter, categoryFilter, tagFilter]);

  const paginatedFAQs = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredFAQs.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredFAQs, currentPage]);

  const toggleSelection = (id) => {
    setSelectedFAQIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedFAQIds.length === filteredFAQs.length) {
      setSelectedFAQIds([]);
    } else {
      setSelectedFAQIds(filteredFAQs.map(f => f.faq_id));
    }
  };

  const handleBulkAction = async (actionType, value = null) => {
    if (selectedFAQIds.length === 0) return;

    setBulkActionLoading(true);
    try {
      const payload = {
        faq_ids: selectedFAQIds,
        actions: [{ type: actionType, value }]
      };

      const response = await ApiService.bulkUpdateFAQs(payload);

      if (response.failed && response.failed.length > 0) {
        toast.error(`Updated ${response.successful.length} FAQs, but ${response.failed.length} failed.`);
      } else {
        toast.success(`Successfully updated ${response.successful.length} FAQs`);
      }

      loadFAQs(); // Refresh list
      setSelectedFAQIds([]);
    } catch (error) {
      toast.error('Bulk action failed');
      console.error(error);
    } finally {
      setBulkActionLoading(false);
    }
  };

  // --- Editor Helpers ---

  const handleCreateNew = () => {
    setEditorForm({
      question: '',
      answer: '',
      status: 'public',
      category_ids: [],
      tags: [],
      author: '',
      needs_review: false
    });
    setCurrentFAQ(null);
    setViewMode('create');
  };

  const handleEdit = (faq) => {
    // Use getFaqCategoryIds to support all field shapes returned by the API:
    // faq.categories (primary), faq.category_ids, or faq.category_id (legacy)
    const catIds = getFaqCategoryIds(faq);
    setEditorForm({
      question: faq.question,
      answer: faq.answer,
      status: faq.status || 'public',
      category_ids: catIds,
      tags: faq.tags || [],
      author: faq.author || '',
      needs_review: faq.needs_review || false
    });
    setCurrentFAQ(faq);
    setViewMode('edit');
  };

  const handleSaveFAQ = async () => {
    if (!editorForm.question.trim() || !editorForm.answer.trim()) {
      toast.error('Question and Answer are required');
      return;
    }

    // Build payload: the backend field for categories is `categories` (not `category_ids`)
    // We map the internal editorForm.category_ids → categories before sending
    const { category_ids, ...rest } = editorForm;
    const apiPayload = {
      ...rest,
      categories: category_ids,
    };

    setLoading(true);
    try {
      if (viewMode === 'create') {
        await ApiService.createFAQ(apiPayload);
        toast.success('FAQ created successfully');
      } else {
        await ApiService.updateFAQ({
          faq_id: currentFAQ.faq_id,
          ...apiPayload
        });
        toast.success('FAQ updated successfully');
      }
      setViewMode('list');
      loadFAQs();
    } catch (error) {
      toast.error(viewMode === 'create' ? 'Failed to create FAQ' : 'Failed to update FAQ');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleTagInputChange = async (e) => {
    const value = e.target.value;
    setTagInput(value);
    if (value.trim().length > 1) {
      try {
        const data = await ApiService.autocompleteTags(value);
        setTagSuggestions(data.tags || []);
      } catch (err) {
        console.error(err);
      }
    } else {
      setTagSuggestions([]);
    }
  };

  const addTag = (tag) => {
    if (!editorForm.tags.includes(tag)) {
      setEditorForm(prev => ({ ...prev, tags: [...prev.tags, tag] }));
    }
    setTagInput('');
    setTagSuggestions([]);
  };

  const removeTag = (tag) => {
    setEditorForm(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  // --- Auto Link Helpers ---
  const addAutoLinkRow = () => {
    setAutoLinks([...autoLinks, { keyword: '', url: '' }]);
  };

  const removeAutoLinkRow = (index) => {
    setAutoLinks(autoLinks.filter((_, i) => i !== index));
  };

  const updateAutoLinkRow = (index, field, value) => {
    const newLinks = [...autoLinks];
    newLinks[index][field] = value;
    setAutoLinks(newLinks);
  };

  const submitAutoLink = async () => {
    const validLinks = autoLinks.filter(l => l.keyword.trim() && l.url.trim());
    if (validLinks.length === 0) {
      toast.error("Please add at least one valid keyword and URL");
      return;
    }

    setShowAutoLinkModal(false);
    await handleBulkAction('auto_link', validLinks);
    setAutoLinks([{ keyword: '', url: '' }]); // Reset
  };

  // --- Optimization Helpers ---
  const optimizationTypes = [
    { value: 'improve_clarity', label: 'Improve Clarity', description: 'Make answers clearer and easier to understand' },
    { value: 'expand_details', label: 'Expand Details', description: 'Add more comprehensive information' },
    { value: 'simplify', label: 'Simplify', description: 'Make content more concise and straightforward' },
    { value: 'seo_optimize', label: 'SEO Optimize', description: 'Optimize for search engine visibility' }
  ];

  const handleBulkOptimizeSubmit = async () => {
    if (!selectedOptimization) {
      toast.error('Please select an optimization type');
      return;
    }

    setShowOptimizationModal(false);
    await handleBulkAction('optimize', { optimization_type: selectedOptimization });
    setSelectedOptimization('');
  };

  // --- Rendering ---

  if (viewMode === 'create' || viewMode === 'edit') {
    return (
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            {viewMode === 'create' ? 'Create New FAQ' : 'Edit FAQ'}
          </h2>
          <button
            onClick={() => setViewMode('list')}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Question</label>
            <input
              type="text"
              value={editorForm.question}
              onChange={e => setEditorForm({ ...editorForm, question: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
              placeholder="e.g. How do I reset my password?"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Answer</label>
            <textarea
              value={editorForm.answer}
              onChange={e => setEditorForm({ ...editorForm, answer: e.target.value })}
              rows={6}
              className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
              placeholder="Provide a detailed answer..."
            />
            <p className="text-xs text-gray-500 mt-1">Markdown is supported.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={editorForm.status}
                onChange={e => setEditorForm({ ...editorForm, status: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
              >
                <option value="draft">Draft</option>
                <option value="public">Public</option>
              </select>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700">Categories</label>
                {categories.length < 50 && (
                  <button
                    type="button"
                    onClick={() => setIsAddingCategory(!isAddingCategory)}
                    className="text-xs font-semibold text-black hover:underline flex items-center gap-1"
                  >
                    {isAddingCategory ? (
                      <><X className="w-3 h-3" /> Cancel</>
                    ) : (
                      <><Plus className="w-3 h-3" /> Add New</>
                    )}
                  </button>
                )}
              </div>

              {editorForm.category_ids && editorForm.category_ids.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-1">
                  {editorForm.category_ids.map(catId => {
                    const cat = categories.find(c => c.category_id === catId);
                    if (!cat) return null;
                    return (
                      <span key={catId} className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border bg-indigo-50 text-indigo-700 border-indigo-200">
                        {cat.name}
                        <button
                          type="button"
                          onClick={() => setEditorForm(prev => ({ ...prev, category_ids: prev.category_ids.filter(id => id !== catId) }))}
                          className="hover:opacity-70 ml-0.5"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}

              {!isAddingCategory ? (
                <select
                  value=""
                  onChange={e => {
                    const val = e.target.value;
                    if (val && editorForm.category_ids && !editorForm.category_ids.includes(val)) {
                      setEditorForm(prev => ({ ...prev, category_ids: [...(prev.category_ids || []), val] }));
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-black/5 focus:border-black outline-none transition-all text-sm bg-white"
                >
                  <option value="">Select a category to add...</option>
                  {categories.filter(c => !(editorForm.category_ids || []).includes(c.category_id)).map(cat => (
                    <option key={cat.category_id} value={cat.category_id}>{cat.name}</option>
                  ))}
                </select>
              ) : (
                <div className="flex flex-col gap-2 bg-gray-50 p-3 rounded-xl border border-gray-200">
                  <div className="text-xs font-semibold text-gray-700">Add New Category</div>
                  <input
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder="Category name..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-black/5 focus:border-black outline-none transition-all text-sm bg-white"
                    autoFocus
                  />
                  <div className="flex justify-end gap-2 mt-1">
                    <button
                      type="button"
                      onClick={() => { setIsAddingCategory(false); setNewCategoryName(''); }}
                      className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-100"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleCreateCategory}
                      disabled={isSavingCategory || !newCategoryName.trim()}
                      className="px-3 py-1.5 bg-black text-white rounded-lg text-xs font-semibold hover:bg-gray-800 disabled:opacity-50 transition-all flex items-center gap-1"
                    >
                      {isSavingCategory ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save Category'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
              <div className="relative">
                <input
                  type="text"
                  value={tagInput}
                  onChange={handleTagInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                  placeholder="Type to search tags..."
                />
                {tagSuggestions.length > 0 && (
                  <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-xl mt-1 shadow-lg max-h-48 overflow-y-auto">
                    {tagSuggestions.map((tag, idx) => (
                      <button
                        key={idx}
                        onClick={() => addTag(tag)}
                        className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm"
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {editorForm.tags.map((tag, idx) => (
                  <span key={idx} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-black">
                    {tag}
                    <button onClick={() => removeTag(tag)} className="ml-1.5 text-black hover:text-gray-700">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="needs_review"
              checked={editorForm.needs_review}
              onChange={e => setEditorForm({ ...editorForm, needs_review: e.target.checked })}
              className="rounded border-gray-300 text-black focus:ring-gray-500"
            />
            <label htmlFor="needs_review" className="text-sm text-gray-700">Flag for Review</label>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <button
              onClick={() => setViewMode('list')}
              className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveFAQ}
              disabled={loading}
              className="px-6 py-2.5 text-sm font-medium text-white bg-black rounded-xl hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {viewMode === 'create' ? 'Create FAQ' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-full overflow-x-hidden">
      {/* Tab Navigation */}
      <div className="flex space-x-1 bg-gray-100/50 p-1 rounded-xl w-fit">
        <button
          onClick={() => setActiveTab('list')}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${activeTab === 'list'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
            }`}
        >
          All FAQs
        </button>
        <button
          onClick={() => setActiveTab('review')}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-all flex items-center gap-2 ${activeTab === 'review'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
            }`}
        >
          Review Center
        </button>
      </div>

      {activeTab === 'list' && (
        <div className="space-y-4 max-w-full overflow-x-hidden">
          {/* Header & Controls */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">FAQ Management</h2>
            <button
              onClick={handleCreateNew}
              className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2 bg-black text-white rounded-xl hover:bg-gray-800 transition-colors shadow-sm"
            >
              <Plus className="h-5 w-5 mr-2" />
              Create FAQ
            </button>
          </div>

          {/* Filters */}
          <div className="flex flex-col gap-3 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-black" />
                <input
                  type="text"
                  placeholder="Search FAQs..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 text-sm"
                />
              </div>

              <div className="w-full sm:w-auto sm:min-w-[150px]">
                <select
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 text-sm bg-white"
                >
                  <option value="all">All Status</option>
                  <option value="public">Public</option>
                  <option value="draft">Draft</option>
                </select>
              </div>
            </div>

            {/* Category Filter */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1 flex items-center gap-1">
                  <Tag className="w-3 h-3" /> Category
                </label>
                <select
                  value={categoryFilter}
                  onChange={e => setCategoryFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 text-sm bg-white"
                >
                  <option value="all">All Categories</option>
                  <option value="none">Uncategorized</option>
                  {categories.map(cat => (
                    <option key={cat.category_id} value={cat.category_id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div className="flex-1">
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1 flex items-center gap-1">
                  <Tag className="w-3 h-3" /> Filter by Tag
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={filterTagInput}
                    onChange={handleFilterTagInputChange}
                    onClick={() => setIsFilterTagDropdownOpen(true)}
                    onFocus={() => setIsFilterTagDropdownOpen(true)}
                    onBlur={() => setIsFilterTagDropdownOpen(false)}
                    placeholder={tagFilter === 'all' ? "Search Tags..." : tagFilter}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 text-sm bg-white"
                  />
                  {isFilterTagDropdownOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      <button
                        onMouseDown={(e) => { e.preventDefault(); selectFilterTag('all'); }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${tagFilter === 'all' ? 'font-semibold text-black' : 'text-gray-700'}`}
                      >
                        All Tags
                      </button>
                      {(filterTagInput.trim() ? filterTagSuggestions : allTags).map(tag => (
                        <button
                          key={tag}
                          onMouseDown={(e) => { e.preventDefault(); selectFilterTag(tag); }}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${tagFilter === tag ? 'font-semibold text-black' : 'text-gray-700'}`}
                        >
                          {tag}
                        </button>
                      ))}
                      {filterTagInput.trim() && filterTagSuggestions.length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-500 text-center">No tags found</div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {(categoryFilter !== 'all' || tagFilter !== 'all') && (
                <div className="flex items-end">
                  <button
                    onClick={() => { setCategoryFilter('all'); selectFilterTag('all'); }}
                    className="px-3 py-2 text-xs text-gray-500 hover:text-black border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap flex items-center gap-1"
                  >
                    <X className="w-3 h-3" /> Clear Filters
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Bulk Actions Toolbar */}
          {selectedFAQIds.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 bg-gray-50 p-3 rounded-xl border border-gray-200 animate-in fade-in slide-in-from-top-2">
              <span className="text-sm font-medium text-black px-2 whitespace-nowrap">
                {selectedFAQIds.length} selected
              </span>

              <div className="hidden sm:block h-6 w-px bg-gray-300 mx-1"></div>

              <button
                onClick={() => handleBulkAction('auto_tag')}
                disabled={bulkActionLoading}
                className="inline-flex items-center px-2.5 py-1.5 text-xs sm:text-sm bg-white border border-gray-200 text-black rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap"
              >
                <Sparkles className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" />
                Auto-Tag
              </button>

              <button
                onClick={() => setShowAutoLinkModal(true)}
                disabled={bulkActionLoading}
                className="inline-flex items-center px-2.5 py-1.5 text-xs sm:text-sm bg-white border border-gray-200 text-black rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap"
              >
                <LinkIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" />
                Auto-Link
              </button>

              <button
                onClick={() => handleBulkAction('optimize_text', 'professional')}
                disabled={bulkActionLoading}
                className="inline-flex items-center px-2.5 py-1.5 text-xs sm:text-sm bg-white border border-gray-200 text-black rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap"
              >
                <Edit3 className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" />
                Optimize
              </button>

              <button
                onClick={() => handleBulkAction('set_status', 'public')}
                disabled={bulkActionLoading}
                className="inline-flex items-center px-2.5 py-1.5 text-xs sm:text-sm bg-white border border-gray-200 text-black rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap"
              >
                <CheckCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" />
                Publish
              </button>

              <button
                onClick={() => handleBulkAction('set_status', 'draft')}
                disabled={bulkActionLoading}
                className="inline-flex items-center px-2.5 py-1.5 text-xs sm:text-sm bg-white border border-gray-200 text-black rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap"
              >
                <FileText className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" />
                Draft
              </button>

              {bulkActionLoading && <Loader2 className="h-5 w-5 animate-spin text-black ml-auto" />}
            </div>
          )}

          {/* FAQ List Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden max-w-full">
            {loading ? (
              <FAQListSkeleton />
            ) : filteredFAQs.length === 0 ? (
              <div className="p-6 sm:p-12 text-center text-gray-500">
                No FAQs found matching your criteria.
              </div>
            ) : (
              <>
                {/* Mobile Card View */}
                <div className="block lg:hidden divide-y divide-gray-100 max-w-full overflow-hidden">
                  {paginatedFAQs.map(faq => (
                    <div key={faq.faq_id} className="p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={selectedFAQIds.includes(faq.faq_id)}
                          onChange={() => toggleSelection(faq.faq_id)}
                          className="mt-1 rounded border-gray-300 text-black focus:ring-gray-500 flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0 overflow-hidden">
                          <div className="font-medium text-gray-900 mb-2 break-words">{faq.question}</div>

                          {faq.tags && faq.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-2 max-w-full">
                              {faq.tags.map(tag => (
                                <span key={tag} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-black break-all">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}

                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            {/* Multi-category display (backwards compatible) */}
                            {(() => {
                              const catIds = getFaqCategoryIds(faq);
                              const matched = catIds.map(id => categories.find(c => c.category_id === id)).filter(Boolean);
                              if (matched.length === 0) return null;
                              return matched.map(cat => (
                                <span key={cat.category_id} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border bg-indigo-50 text-indigo-700 border-indigo-200">
                                  {cat.name}
                                </span>
                              ));
                            })()}
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              (faq.status === 'public' || !faq.status) ? 'bg-gray-100 text-black border border-gray-200' : 'bg-gray-100 text-black border border-gray-200'
                            }`}>
                              {(faq.status === 'public' || !faq.status) ? 'Public' : 'Draft'}
                            </span>
                            {faq.needs_review && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                                Review
                              </span>
                            )}
                          </div>

                          <div className="flex flex-wrap items-center gap-3 text-xs sm:text-sm text-gray-500 mb-2">
                            <span className="whitespace-nowrap">Views: {faq.view_count || 0}</span>
                            <span className="flex items-center text-black whitespace-nowrap">
                              <ThumbsUp className="h-3 w-3 mr-1" /> {faq.helpful_votes || 0}
                            </span>
                            <span className="flex items-center text-black whitespace-nowrap">
                              <ThumbsDown className="h-3 w-3 mr-1" /> {faq.not_helpful_votes || 0}
                            </span>
                          </div>

                          <button
                            onClick={() => handleEdit(faq)}
                            className="inline-flex items-center px-3 py-1.5 text-sm text-black hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <Edit3 className="h-3.5 w-3.5 mr-1.5" />
                            Edit
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop Table View */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="w-full text-left">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 xl:px-6 py-3 w-12">
                          <input
                            type="checkbox"
                            checked={selectedFAQIds.length === filteredFAQs.length && filteredFAQs.length > 0}
                            onChange={toggleSelectAll}
                            className="rounded border-gray-300 text-black focus:ring-gray-500"
                          />
                        </th>
                        <th className="px-4 xl:px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Question</th>
                        <th className="px-4 xl:px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</th>
                        <th className="px-4 xl:px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-4 xl:px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Views</th>
                        <th className="px-4 xl:px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Stats</th>
                        <th className="px-4 xl:px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {paginatedFAQs.map(faq => (
                        <tr key={faq.faq_id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 xl:px-6 py-4">
                            <input
                              type="checkbox"
                              checked={selectedFAQIds.includes(faq.faq_id)}
                              onChange={() => toggleSelection(faq.faq_id)}
                              className="rounded border-gray-300 text-black focus:ring-gray-500"
                            />
                          </td>
                          <td className="px-4 xl:px-6 py-4">
                            <div className="font-medium text-gray-900 line-clamp-2">{faq.question}</div>
                            {faq.tags && faq.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {faq.tags.map(tag => (
                                  <span key={tag} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-black">
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="px-4 xl:px-6 py-4">
                            {(() => {
                              const catIds = getFaqCategoryIds(faq);
                              const matched = catIds.map(id => categories.find(c => c.category_id === id)).filter(Boolean);
                              if (matched.length === 0) return <span className="text-gray-400 text-xs italic">No Category</span>;
                              return (
                                <div className="flex flex-wrap gap-1">
                                  {matched.map(cat => (
                                    <span key={cat.category_id} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-indigo-50 text-indigo-700 border-indigo-200">
                                      {cat.name}
                                    </span>
                                  ))}
                                </div>
                              );
                            })()}
                          </td>
                          <td className="px-4 xl:px-6 py-4">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-black border border-gray-200">
                              {(faq.status === 'public' || !faq.status) ? 'Public' : 'Draft'}
                            </span>
                            {faq.needs_review && (
                              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-black border border-gray-200">
                                Review
                              </span>
                            )}
                          </td>
                          <td className="px-4 xl:px-6 py-4 text-sm text-gray-500">
                            {faq.view_count || 0}
                          </td>
                          <td className="px-4 xl:px-6 py-4">
                            <div className="flex items-center gap-3 text-sm text-gray-500">
                              <span className="flex items-center text-black">
                                <ThumbsUp className="h-3 w-3 mr-1" /> {faq.helpful_votes || 0}
                              </span>
                              <span className="flex items-center text-black">
                                <ThumbsDown className="h-3 w-3 mr-1" /> {faq.not_helpful_votes || 0}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 xl:px-6 py-4 text-right">
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={() => handleEdit(faq)}
                                className="p-2 text-gray-400 hover:text-black hover:bg-gray-100 rounded-lg transition-colors"
                                title="Edit"
                              >
                                <Edit3 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {/* Pagination Controls */}
            {filteredFAQs.length > 0 && (
              <div className="bg-white border-t border-gray-200 px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-3 rounded-b-xl">
                <div className="text-xs sm:text-sm text-gray-500 text-center sm:text-left">
                  Showing <span className="font-medium">{Math.min((currentPage - 1) * itemsPerPage + 1, filteredFAQs.length)}</span> to <span className="font-medium">{Math.min(currentPage * itemsPerPage, filteredFAQs.length)}</span> of <span className="font-medium">{filteredFAQs.length}</span> results
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(Math.ceil(filteredFAQs.length / itemsPerPage), p + 1))}
                    disabled={currentPage >= Math.ceil(filteredFAQs.length / itemsPerPage)}
                    className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showAutoLinkModal && (
        <div className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-xl max-w-lg w-full p-4 sm:p-6 shadow-xl my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-base sm:text-lg font-bold text-gray-900">Auto-Link Keywords</h3>
              <button onClick={() => setShowAutoLinkModal(false)} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                <X className="h-5 w-5" />
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Automatically add hyperlinks to keywords in the selected FAQs.
            </p>

            <div className="space-y-3 max-h-[50vh] overflow-y-auto mb-4">
              {autoLinks.map((link, idx) => (
                <div key={idx} className="flex flex-col sm:flex-row gap-2 items-start">
                  <div className="flex-1 w-full">
                    <input
                      type="text"
                      placeholder="Keyword"
                      value={link.keyword}
                      onChange={(e) => updateAutoLinkRow(idx, 'keyword', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div className="flex-1 w-full">
                    <input
                      type="text"
                      placeholder="URL"
                      value={link.url}
                      onChange={(e) => updateAutoLinkRow(idx, 'url', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  {autoLinks.length > 1 && (
                    <button
                      onClick={() => removeAutoLinkRow(idx)}
                      className="p-2 text-black hover:bg-gray-100 rounded-lg flex-shrink-0"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 pt-3 border-t border-gray-100">
              <button
                onClick={addAutoLinkRow}
                className="text-sm text-black hover:text-gray-700 font-medium flex items-center justify-center gap-1 px-3 py-2"
              >
                <Plus className="h-4 w-4" /> Add Another
              </button>

              <div className="flex gap-2">
                <button
                  onClick={() => setShowAutoLinkModal(false)}
                  className="flex-1 sm:flex-none px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={submitAutoLink}
                  className="flex-1 sm:flex-none px-4 py-2 text-sm bg-black text-white hover:bg-gray-800 rounded-lg"
                >
                  Apply Links
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Optimization Modal */}
      {showOptimizationModal && (
        <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-xl max-w-2xl w-full p-4 sm:p-6 shadow-xl my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4 sm:mb-6">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900">Optimize Selected FAQs</h3>
              <button onClick={() => setShowOptimizationModal(false)} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                <X className="h-6 w-6" />
              </button>
            </div>

            <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
              Select how you would like to optimize the {selectedFAQIds.length} selected FAQs. AI will process your request.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8">
              {optimizationTypes.map((type) => (
                <div key={type.value} className="relative">
                  <input
                    type="radio"
                    id={type.value}
                    name="optimization"
                    value={type.value}
                    checked={selectedOptimization === type.value}
                    onChange={(e) => setSelectedOptimization(e.target.value)}
                    className="sr-only"
                  />
                  <label
                    htmlFor={type.value}
                    className={`block p-3 sm:p-4 border rounded-xl cursor-pointer transition-all duration-300 hover:shadow-md ${selectedOptimization === type.value
                        ? 'border-black bg-gray-50 shadow-md ring-1 ring-black'
                        : 'border-gray-200 hover:border-black bg-white'
                      }`}
                  >
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-gray-900 text-sm sm:text-base">{type.label}</h4>
                      {selectedOptimization === type.value && (
                        <CheckCircle className="h-5 w-5 text-black flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600 mt-2 leading-relaxed">{type.description}</p>
                  </label>
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
              <button
                onClick={() => setShowOptimizationModal(false)}
                className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkOptimizeSubmit}
                disabled={!selectedOptimization}
                className="px-6 py-2.5 text-sm font-medium text-white bg-black rounded-xl hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2"
              >
                <Sparkles className="h-4 w-4" />
                Optimize FAQs
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'review' && (
        <ReviewCenter
          onEdit={handleEdit}
          onCreateDraft={(query) => {
            handleCreateNew();
            setEditorForm(prev => ({ ...prev, question: query, status: 'draft' }));
          }}
        />
      )}

    </div>
  );
}

export default FAQManagement;
